import { fetchAuthSession, signOut, getCurrentUser } from "@aws-amplify/auth";
import { AUTH_API } from "./api";
import { fetchWithAuth } from "./api";

export const getAuthToken = async () => {
  try {
    const session = await fetchAuthSession();
    
    // Log session details for debugging
    console.log('Session Details:', {
      hasTokens: !!session?.tokens,
      hasIdToken: !!session?.tokens?.idToken,
      tokenType: typeof session?.tokens?.idToken
    });

    if (!session?.tokens?.idToken) {
      throw new Error("No valid authentication token found");
    }

    // Convert to string and clean the token
    let token = session.tokens.idToken.toString();
    
    // Additional cleaning and validation
    token = token
      .trim()           // Remove whitespace
      .replace(/"/g, '') // Remove any quotes
      .replace(/^Bearer\s*/i, ''); // Remove 'Bearer ' prefix if present

    // Log token details (without revealing full token)
    console.log('Token Details:', {
      length: token.length,
      firstTwenty: token.substring(0, 20) + '...',
      lastTwenty: '...' + token.substring(token.length - 20)
    });

    // Validate token format
    if (!token.match(/^[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+$/)) {
      throw new Error('Token does not appear to be a valid JWT');
    }

    return token;
  } catch (error) {
    console.error("Detailed Error getting auth token:", {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
};

export const handleSignOut = async (navigate) => {
  try {
    // Clear auth state immediately to prevent redirect loops
    localStorage.removeItem('auth_user');
    
    await signOut();
    navigate("/auth?state=signIn");
  } catch (error) {
    console.error("Error signing out:", error);
    // Even if signOut fails, clear local state and redirect
    localStorage.removeItem('auth_user');
    navigate("/auth?state=signIn");
  }
};

export const fetchUserProfile = async (role) => {
  try {
    // Get current user from Cognito
    const currentUser = await getCurrentUser();
    const session = await fetchAuthSession();
    
    // Extract user info from token claims
    const claims = session.tokens.idToken.payload;
    
    // Try to get user profile from API first
    try {
      const token = session.tokens.idToken.toString();
      const response = await fetch(`${AUTH_API}/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const profileData = await response.json();
        console.log('Profile data from API:', profileData);
        
        // If we have firstName and lastName from the API, use them
        if (profileData.firstName || profileData.lastName) {
          const name = `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim();
          return {
            name: name || 'Account User',
            email: profileData.email || claims.email || 'user@example.com',
            role: profileData.role || claims['custom:role'] || role,
          };
        }
      }
    } catch (apiError) {
      console.error('Error fetching profile from API:', apiError);
      // Continue with token claims if API call fails
    }
    
    // Fallback to token claims if API call fails or doesn't return name
    let displayName = 'Account User';
    if (claims.given_name && claims.family_name) {
      displayName = `${claims.given_name} ${claims.family_name}`.trim();
    } else if (claims.given_name) {
      displayName = claims.given_name.trim();
    } else if (claims.name && !claims.name.includes('@') && claims.name !== claims['cognito:username']) {
      displayName = claims.name.trim();
    }
    
    // Return formatted user info
    return {
      name: displayName,
      email: claims.email || 'user@example.com',
      role: claims['custom:role'] || role,
    };
  } catch (error) {
    console.error('Error in profile API call:', error);
    // Return default values if fetch fails
    return {
      name: 'Account User',
      email: 'user@example.com',
      role: role || 'user'
    };
  }
}; 